import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { StorageService } from '@shared/services/storage.service';
import { Router } from '@angular/router';
import { AuthUser, LoggedInUser, TokensPayload } from '../models/admin.auth.model';
import { environment } from '@environments/environment';
import { jwtDecode } from 'jwt-decode';
import { AppConstants } from '@admin/configs/admin.app.constants';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  appUri = environment.apiUrl;
  authTokens: TokensPayload | null = this.getToken() || null;
  constructor(
    private readonly http: HttpClient,
    private readonly storageService: StorageService,
    private readonly router: Router
  ) {}

  login(authUser: AuthUser) {
    return this.http.post(
      `${this.appUri}/connect/login?requestedPortal=2`,
      authUser
    );
  }

  getToken() {
    const tokens = this.storageService.getItem('admin_tokens');
    return (tokens ? JSON.parse(tokens) : null) as TokensPayload | null;
  }

  setToken(tokens: TokensPayload) {
    this.authTokens = tokens;
    return this.storageService.setItem('admin_tokens', JSON.stringify(tokens));
  }

  logout(): void {
    if (typeof window !== 'undefined') {
      this.storageService.removeItem('admin_tokens');
      this.router.navigate([`${AppConstants.adminBasePath}login`]);
    }
  }

  confirmEmail(email: string, token: string) {
    const paramsData = {
      email: encodeURIComponent(email),
      token: encodeURIComponent(token),
    };
    const params: HttpParams = new HttpParams().appendAll(paramsData);
    return this.http.get(`${this.appUri}/account/confirm-email`, {
      params,
    });
  }

  forgotPassword(email: string) {
    return this.http.post(
      `${this.appUri}/account/forgot-password?requestedPortal=2&email=${email}`,
      email
    );
  }

  resetPassword(email: string, password: string, code: string) {
    const body = {
      email: encodeURIComponent(email),
      password: password,
      confirmPassword: password,
      code: encodeURIComponent(code),
    };
    return this.http.post(`${this.appUri}/account/reset-password`, body);
  }

  refreshToken(access_token: string, refreshToken: string) {
    return this.http.post(`${this.appUri}/connect/refresh`, {
      access_Token: access_token,
      refresh_Token: refreshToken,
    });
  }

  twoFactorAuth(email: string, code: string) {
    return this.http.post(`${this.appUri}/connect/verify-2fa`, {
      email,
      code,
    });
  }

  resend2FA(email: string) {
    return this.http.post(
      `${this.appUri}/connect/verify-2fa?email=${email}`,
      {}
    );
  }

  getLoggedInProfileData() {
    const token = this.getToken();
    if(!token) return '';
    const userProfile = jwtDecode(JSON.stringify(token));
    return userProfile;
  }

  getLoggedInUserRoles(): string[] {
    const userData = this.getLoggedInProfileData() as LoggedInUser;
    if(!userData) return [];
    
    if (userData?.role) {
      return Array.isArray(userData.role) ? userData.role : [userData.role];
    }
    return [];
  }

  setPassword(email: string, password: string, token: string) {
    const body = {
      email: encodeURIComponent(email),
      password,
      confirmPassword: password,
      token: encodeURIComponent(token),
    };
    return this.http.post(
      `${this.appUri}/account/set-password?requestedPortal=2`,
      body
    );
  }
}
